import React, { useContext } from 'react'

import { UserContext } from '../../contexts';
import { PageHeader, PageLayout } from '../../components/layout';
import { isActiveStoreOwner } from '../../helpers/adminAccess';

// Fontaine M57A — the settings shell is the shared page chrome (PageLayout +
// PageHeader with underline tabs), replacing the page-local `.container` +
// `users-tab-bar` pair. Which tabs render, their targets and their gates are
// unchanged; active-tab detection stays exact-or-subpath (PageHeader/Tabs
// implement the same rule). `users-dark` stays on the shell so the settings
// organisms keep their existing cascade until their own milestones.
// "Shopify App" (/shopify-settings) was removed for everyone 2026-10-30 (F8);
// the URL still renders for RC / DAO staff — see ShopifySettingsPage.
const BASE_TABS = [
  { label: 'Store Settings', link: '/store-settings' },
];

// The store owner's own controls (plan, feeds, accounting, branding,
// marketplace accounts…). URL is /owner-admin; the earlier spellings
// (/primary-admin, /master-admin-settings) redirect here — see Routes.jsx.
const PRIMARY_ADMIN_TAB = { label: 'Owner Admin', link: '/owner-admin' };
// Platform governance (MCP Access, Store Admin).
const MASTER_ADMIN_TAB = { label: 'Retail Circle Admin', link: '/retail-circle-admin' };
// Cooby VM WhatsApp Web sessions — re-link a dropped seat from a phone instead
// of Remote Desktop. RC staff only, and not entitlement-gated: these are
// our own two ingestion VMs, not a per-store feature. The QR it renders links a device to a live WhatsApp account.
const WHATSAPP_VMS_TAB = { label: 'WhatsApp VMs', link: '/whatsapp-vms' };

/**
 * @param {boolean} [legacy=true]  false = render WITHOUT the legacy `users-dark rc-settings`
 *   cascade. That cascade (_rc-legacy-darkmode.scss, rc-settings rules) restyles every
 *   heading, label, input and button inside it, which is right for the old settings
 *   organisms and wrong for pages built entirely from Fontaine primitives — it double-boxed
 *   inputs, hid button labels and mixed typefaces on WhatsApp Broadcast. New pages opt out.
 */
export function SettingsTemplate({ children, legacy = true }) {
  const { user, accessibleStores, activeStoreId } = useContext(UserContext);

  // Owner Admin (the store's own controls — plan, feeds, accounting,
  // branding, marketplace accounts, shipping, Xero) shows only to the ACTIVE
  // store's owner and RC master admins — the same isActiveStoreOwner() gate
  // the page itself applies (MasterAdminSettings), so nobody is offered a
  // tab that opens on "Access denied". Retail Circle Admin (platform
  // governance — MCP Access, Store Admin) and WhatsApp VMs stay RC-staff
  // only, and their routes are requireMasterAdmin. The backend still gates
  // each action (platform endpoints via EnsureMasterAdmin, per-store
  // settings by same-store policy).
  // The WhatsApp tab (/whatsapp-settings, the orchestrator dashboard) was
  // removed 2026-10-29; the route now redirects to /store-settings.
  const tabs = [...BASE_TABS];
  if (user?.is_admin && isActiveStoreOwner(user, accessibleStores, activeStoreId)) tabs.push(PRIMARY_ADMIN_TAB);
  // WhatsApp Broadcast left these tabs for its own page in the sidebar (2026-10-16).
  // WhatsApp VMs sits before Retail Circle Admin: it is an operational tab
  // someone reaches for when a seat has dropped, so it should not be behind
  // the platform-governance one.
  if (user?.is_master_admin) {
    tabs.push(WHATSAPP_VMS_TAB);
    tabs.push(MASTER_ADMIN_TAB);
  }

  return (
    <PageLayout className={legacy ? "users-dark rc-settings" : "rc-settings-modern"}>
      <PageHeader
        title="Settings"
        description="Store details, integrations and admin controls"
        showBackButton={false}
        tabs={tabs.map(({ label, link }) => ({ label, to: link }))}
      />
      {children}
    </PageLayout>
  );
}
